
import React from "react";
import * as ReactRouterDom from "react-router-dom";
import { StringHelper } from "@rivtn/noseur";
import Btn from "./Btn";
import { statusLabel } from "./TournamentCard";
import { i18nManager } from "../../i18n";
import { Utils } from "../../../../../utils";
import styles from "../../assets/style.module.css";
import { editionDates, prizePools } from "../../helpers";
import { i18nManager as globalI18nManager } from "../../../../../i18n";
import { BaseService, ITournament, ITournamentEdition, OmneHostService, SanitizedResponse, TournamentStatus } from "../../../../../services";

export interface TournamentDetailProps {
    hostId: string;
    tournament: ITournament;
    onBack: () => void;
}

function TournamentDetail({ hostId, tournament: t, onBack }: TournamentDetailProps) {

    const labels = i18nManager.Labels.tournaments;
    const edition = t.tournament_edition;
    const registered = edition?.meta?.total_registered_participants ?? 0;
    const expected = edition?.meta?.total_expected_participants ?? 0;
    const pct = expected ? Math.min(100, Math.round((registered / expected) * 100)) : 0;
    const [editions, setEditions] = React.useState<ITournamentEdition[]>();
    const facts: [string, string | undefined][] = [
        [labels.team_size, edition?.participant_members_count ? `${edition.participant_members_count}v${edition.participant_members_count}` : undefined],
        [labels.location, edition?.location],
        [labels.date, editionDates(edition)],
        [labels.capacity, expected ? `${registered}/${expected} · ${pct}%` : undefined],
        [labels.program, edition?.meta?.program_name],
        [labels.prize_pool, prizePools(edition).join(" · ") || undefined],
    ];

    React.useEffect(() => {
        window.scrollTo({ top: 0 });
        OmneHostService.getInstance().queryHostTournamentEditions(hostId, {
            size: 20,
            sort: "updated_at,DESC",
            tournament_id: t.external_id,
            status__in: ["UPCOMING", "ONGOING", "ENDED"],
        }).then(({ content }: SanitizedResponse<ITournamentEdition>) => {
            setEditions(content ?? []);
        }).catch(BaseService.reportError);
    }, [hostId, t.external_id]);

    return (<section className={styles["section-page"]}>
        <div className={styles["container"]}>
            <button className={styles["back-btn"]} onClick={onBack}><i className="fa fa-arrow-left" /> {labels.back}</button>
            <div className={styles["detail-grid"]}>
                <div className={styles["detail-media"]}>
                    {edition?.banner_url ? (<img src={edition.banner_url} alt={t.name} />) : (<span className={styles["img-ph"]} />)}
                </div>
                <div className={styles["detail-body"]}>
                    <div className={styles["detail-head"]}>
                        {edition?.logo_url && (<img className={styles["detail-logo"]} src={edition.logo_url} alt="" />)}
                        <span className={styles["chip"]}>{statusLabel(edition?.status)}</span>
                    </div>
                    <h1 className={styles["h-03"]}>{t.name}</h1>
                    {edition?.name && (<span className={styles["tag"]}>{edition.name}</span>)}
                    {t.description && (<p className={styles["lead"]}>{t.description}</p>)}
                    <dl className={styles["facts"]}>
                        {facts.filter(([_, v]) => !!v).map(([k, v]) => (<div key={k}>
                            <dt className={styles["tag"]}>{k}</dt>
                            <dd>{v}</dd>
                        </div>))}
                    </dl>
                    {expected > 0 && (<div className={styles["capacity"]}>
                        <div className={styles["capacity-bar"]}><span style={{ width: `${pct}%` }} /></div>
                        <div className={styles["capacity-txt"]}>
                            <span>{registered} {labels.registered}</span>
                            <span>{Math.max(0, expected - registered)} {labels.spots_left}</span>
                        </div>
                    </div>)}
                    {edition && (<Btn target="_blank" to={{ pathname: `/to/${t.public_id}`, search: "edi=" + edition.external_id + (edition.status === TournamentStatus.UPCOMING ? "&register" : "") }}>
                        {edition.status === TournamentStatus.UPCOMING ? labels.register : labels.visit}
                    </Btn>)}
                </div>
            </div>

            {!!editions?.length && (<div className={styles["block"]}>
                <span className={styles["tag"]}>{labels.editions}</span>
                <div className={styles["table-wrap"]}>
                    <table className={styles["table"]}>
                        <thead>
                            <tr>
                                <th>{labels.edition}</th>
                                <th>{labels.date}</th>
                                <th>{labels.winner}</th>
                                <th>{labels.prize}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {editions.map((ed, i) => {
                                const currency = ed.meta?.winner_currency;
                                const amount = currency ? ed.meta?.price_pool_amounts?.[currency] : undefined;
                                return (<tr key={ed.external_id}>
                                    <td><ReactRouterDom.NavLink target="_blank" to={{ pathname: `/to/${t.public_id}`, search: "edi=" + ed.external_id }}>#{editions.length - i} {ed.name}</ReactRouterDom.NavLink></td>
                                    <td>{ed.dates?.tournament_start_date ? Utils.fineDateShort(new Date(ed.dates.tournament_start_date)) : "-"}</td>
                                    <td>{ed.meta?.winner_name ?? "-"}</td>
                                    <td>{(currency && amount !== undefined) ? `${globalI18nManager.queryCountryCurrency(currency, "currency_code", "currency_sign")} ${StringHelper.toPresentableMoneyValue(amount)}` : "-"}</td>
                                </tr>);
                            })}
                        </tbody>
                    </table>
                </div>
            </div>)}
        </div>
    </section>);
}

export default TournamentDetail;
